import { useCallback, useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import { useVisiblePolling } from '../hooks/useVisiblePolling';
import { api } from '../api/client';
import './UsageWidget.css';

// Anthropic account-limit usage per login profile (#426, Alex: "sehen was mein
// usage status ist"). Backend: cloud-api GET /api/account-usage, addressed
// absolutely against arkserver + bearer-authenticated — see api.accountUsage()
// for why (master tokens are arkserver-only; auth required since #483).

// Traffic light (Cloud's spec): green <70, amber <90, red >=90.
type Level = 'ok' | 'warn' | 'crit';
const levelOf = (pct: number | null | undefined): Level =>
  pct == null ? 'ok' : pct >= 90 ? 'crit' : pct >= 70 ? 'warn' : 'ok';

interface UsageWindow {
  utilization?: number | null;
  resets_at?: string | null;
}
interface UsageProfile {
  profile?: string;
  label?: string;
  ok?: boolean;
  error?: string | null;
  seven_day?: UsageWindow | null;
  five_hour?: UsageWindow | null;
  extra_usage?: {
    utilization?: number | null;
    is_enabled?: boolean;
    disabled_reason?: string | null;
  } | null;
}

interface CodexAccount {
  email?: string;
  plan?: string;
  used_percent?: number | null;
  window_minutes?: number | null;
  resets_at?: number | null;      // UNIX epoch (seconds) — NOT an ISO string
  credits?: { has_credits?: boolean; unlimited?: boolean; balance?: string | null } | null;
  rate_limit_reached?: unknown;
  age_seconds?: number | null;
  source_bot?: string | null;
  // Cloud 2e46168: which account is the real one is now DERIVED server-side
  // from the host login (~/.codex/auth.json that the bot homes symlink to),
  // not from a hardcoded address — so an account switch carries itself and no
  // person's e-mail lives in this file. The array arrives sorted (primary,
  // then bot_count, then email); we keep that order and only mark emphasis.
  primary?: boolean;
  bot_count?: number | null;
}

// The Codex window is not fixed: 10080 min = 7 days (pro), 43200 = 30 days
// (plus). Derive the label instead of hardcoding one.
function windowLabel(mins: number | null | undefined): string {
  if (!mins) return 'Fenster';
  const d = Math.round(mins / 1440);
  if (d >= 1) return `${d} ${d === 1 ? 'Tag' : 'Tage'}`;
  const h = Math.round(mins / 60);
  return `${h} ${h === 1 ? 'Stunde' : 'Stunden'}`;
}

// "vor 3 h" — how old the snapshot is. OpenAI offers no usage endpoint, so
// these values are as fresh as the account's last Codex turn (can be ~24 h).
// Showing this is a correctness requirement, not polish: without it the widget
// implies a currency it does not have.
function ageLabel(sec: number | null | undefined): string {
  if (sec == null) return '';
  if (sec < 90) return 'gerade eben';
  const m = Math.round(sec / 60);
  if (m < 60) return `vor ${m} min`;
  const h = Math.round(m / 60);
  if (h < 48) return `vor ${h} h`;
  return `vor ${Math.round(h / 24)} d`;
}

// Accepts BOTH shapes on purpose: the Anthropic block sends an ISO string, the
// Codex block a UNIX epoch in SECONDS (verified against the live payload:
// `"resets_at": 1786176822` in the Codex rollout jsonl). Multiplying is not
// cosmetic — reading epoch seconds as milliseconds lands in 1970.
function toMs(at: string | number | null | undefined): number | null {
  if (at == null || at === '') return null;
  const t = typeof at === 'number' ? at * 1000 : Date.parse(at);
  return Number.isNaN(t) ? null : t;
}

// "heute 19:40" / "morgen 08:15" / "Sa 08.08., 10:13" — the ABSOLUTE moment the
// window refills, which is what Alex asked for ("wann die quota nach 7 Tagen
// refreshed"). On a 7-day window a bare distance like "in 4 d" does not answer
// it: you want to know WHICH day the capacity is back, not how long you wait.
// Near-term resets get the friendlier form because a weekday is noise when the
// answer is "tonight".
function resetLabel(at: string | number | null | undefined): string {
  const t = toMs(at);
  if (t == null) return '';
  if (t <= Date.now()) return 'jetzt';
  const d = new Date(t);
  const hhmm = d.toLocaleTimeString('de-AT', { hour: '2-digit', minute: '2-digit' });
  // Calendar-day distance, NOT hours/24: at 23:00 a reset six hours later is
  // "morgen", and an hour-based rule would call it "heute".
  const midnight = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const days = Math.round((midnight(d) - midnight(new Date())) / 86400000);
  if (days <= 0) return `heute ${hhmm}`;
  if (days === 1) return `morgen ${hhmm}`;
  // Aus Teilen zusammengesetzt statt in einem Aufruf: de-AT hängt an den
  // Kurz-Wochentag selbst einen Punkt, zusammen mit dem Komma des Datums
  // ergäbe das "Sa., 08.08., 10:13".
  const wd = d.toLocaleDateString('de-AT', { weekday: 'short' }).replace('.', '');
  const date = d.toLocaleDateString('de-AT', { day: '2-digit', month: '2-digit' });
  return `${wd} ${date} ${hhmm}`;
}

// "in 3 h 20 min" / "in 4 d" — the same moment as a distance. Kept alongside
// the absolute form: the distance is the glanceable one, the date the precise
// one, and the tooltip shows both.
function untilLabel(at: string | number | null | undefined): string {
  const t = toMs(at);
  if (t == null) return '';
  const mins = Math.round((t - Date.now()) / 60000);
  if (mins <= 0) return 'jetzt';
  if (mins < 60) return `in ${mins} min`;
  const h = Math.floor(mins / 60);
  if (h < 24) return mins % 60 ? `in ${h} h ${mins % 60} min` : `in ${h} h`;
  return `in ${Math.floor(h / 24)} d ${h % 24} h`;
}

const fmtPct = (p: number | null | undefined) =>
  p == null ? '–' : `${Math.round(p)}%`;

function Metric({ label, win }: { label: string; win: UsageWindow | null | undefined }) {
  const pct = win?.utilization;
  const reset = win?.resets_at;
  const ms = toMs(reset);
  const title = ms
    ? `${label}: ${fmtPct(pct)} — Reset ${resetLabel(reset)} (${untilLabel(reset)}, ${new Date(ms).toLocaleString('de-AT')})`
    : `${label}: ${fmtPct(pct)}`;
  return (
    <span className={`uw-metric uw-${levelOf(pct)}`} title={title}>
      <span className="uw-metric-label">{label}</span>
      {/* Bar + number: the bar is the glanceable part, the % the precise one. */}
      <span className="uw-bar" aria-hidden="true">
        <span className="uw-bar-fill" style={{ width: `${Math.min(100, Math.max(0, pct ?? 0))}%` }} />
      </span>
      <span className="uw-pct">{fmtPct(pct)}</span>
    </span>
  );
}

// One muted line naming when each window refills. Windows without a reset
// timestamp are dropped rather than rendered as "—": an empty slot invites the
// reader to assume "no reset", when the truth is "backend didn't say".
function ResetLine({ entries }: { entries: Array<[string, string | number | null | undefined]> }) {
  const shown = entries
    .map(([label, at]) => [label, resetLabel(at), untilLabel(at)] as const)
    .filter(([, when]) => when !== '');
  if (shown.length === 0) return null;
  return (
    <div className="uw-age">
      {shown.map(([label, when, dist], i) => (
        <span key={label} title={`${label}-Fenster füllt sich wieder ${when} (${dist})`}>
          {i > 0 && <span className="uw-sep"> · </span>}
          Reset {label} {when}
        </span>
      ))}
    </div>
  );
}

function CodexRow({ acc, primary }: { acc: CodexAccount; primary: boolean }) {
  const pct = acc.used_percent;
  // A tripped rate limit outranks the percentage: the number can read harmless
  // while the account is actually locked out.
  const limited = acc.rate_limit_reached != null && acc.rate_limit_reached !== false;
  const level: Level = limited ? 'crit' : levelOf(pct);
  const win = windowLabel(acc.window_minutes);
  const age = ageLabel(acc.age_seconds);
  const reset = untilLabel(acc.resets_at);
  const resetAt = resetLabel(acc.resets_at);
  const name = acc.email || acc.source_bot || 'Codex';
  const title = [
    `${name}${acc.plan ? ` · ${acc.plan}` : ''}`,
    acc.bot_count != null && `${acc.bot_count} ${acc.bot_count === 1 ? 'Bot' : 'Bots'} an diesem Konto`,
    primary && 'Konto des Host-Logins',
    `${win}: ${fmtPct(pct)}`,
    resetAt && `Reset ${resetAt} (${reset})`,
    acc.age_seconds != null && `Stand ${age} (Snapshot aus dem letzten Codex-Turn)`,
    acc.source_bot && `Quelle: ${acc.source_bot}`,
  ].filter(Boolean).join('\n');

  return (
    <div className={`uw-row${primary ? '' : ' uw-row-secondary'}`}>
      <div className="uw-name" title={title}>
        {name}
        {acc.plan && <span className="uw-plan">{acc.plan}</span>}
        {/* Bot count makes the weight legible instead of asking the reader to
            trust the ordering — Cloud's point, and a good one. */}
        {acc.bot_count != null && <span className="uw-bots">{acc.bot_count} Bots</span>}
        {limited && (
          <span className="uw-badge" title="Rate-Limit erreicht">
            <AlertTriangle size={9} /> limit
          </span>
        )}
      </div>
      <div className="uw-metrics">
        <span className={`uw-metric uw-${level}`} title={title}>
          <span className="uw-metric-label">{win}</span>
          <span className="uw-bar" aria-hidden="true">
            <span className="uw-bar-fill" style={{ width: `${Math.min(100, Math.max(0, pct ?? 0))}%` }} />
          </span>
          <span className="uw-pct">{fmtPct(pct)}</span>
        </span>
      </div>
      {/* Snapshot age AND reset are shown inline, not just in the tooltip — a
          24h-old value that looks live is worse than no value, and a quota bar
          without its refill date leaves the only actionable question open. */}
      {(age || resetAt) && (
        <div className="uw-age">
          {age && (
            <span title="OpenAI bietet keinen Usage-Abruf; der Wert stammt aus dem letzten Codex-Turn">
              Stand {age}
            </span>
          )}
          {age && resetAt && <span className="uw-sep"> · </span>}
          {resetAt && (
            <span title={`Fenster füllt sich wieder ${reset} (${new Date(toMs(acc.resets_at) as number).toLocaleString('de-AT')})`}>
              Reset {resetAt}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

export function UsageWidget() {
  const [profiles, setProfiles] = useState<UsageProfile[] | null>(null);
  const [codex, setCodex] = useState<CodexAccount[]>([]);
  const [failed, setFailed] = useState(false);

  const load = useCallback(async () => {
    try {
      const d = await api.accountUsage();
      setProfiles(Array.isArray(d.profiles) ? d.profiles : []);
      // Server already sorts (primary → bot_count → email); take that order as-is.
      setCodex(Array.isArray(d.codex) ? d.codex : []);
      setFailed(false);
    } catch {
      // Keep the last good values on screen rather than blanking the widget —
      // a transient federation hiccup shouldn't erase the numbers.
      setFailed(true);
    }
  }, []);

  // ~60s per Cloud's spec; the hook backs off automatically on a hidden tab.
  useVisiblePolling(load, 60000);

  // Nothing yet and nothing to show — stay invisible instead of rendering an
  // empty frame (this sits in the sidebar footer, space is scarce).
  if ((!profiles || profiles.length === 0) && codex.length === 0) return null;

  return (
    <div className="usage-widget">
      <div className="uw-head">
        <span>Usage</span>
        {failed && <span className="uw-stale" title="Letzter Abruf fehlgeschlagen — angezeigte Werte sind veraltet">stale</span>}
      </div>
      {(profiles ?? []).map((p) => {
        const name = p.label || p.profile || '?';
        const ooc = p.extra_usage?.disabled_reason === 'out_of_credits';
        if (p.ok === false) {
          return (
            <div className="uw-row" key={name}>
              <div className="uw-name" title={p.error || 'Abruf fehlgeschlagen'}>{name}</div>
              <div className="uw-err">nicht abrufbar</div>
            </div>
          );
        }
        return (
          <div className="uw-row" key={name}>
            <div className="uw-name" title={name}>
              {name}
              {ooc && (
                <span className="uw-badge" title="Extra-Usage deaktiviert: out_of_credits">
                  <AlertTriangle size={9} /> credits
                </span>
              )}
            </div>
            <div className="uw-metrics">
              <Metric label="7d" win={p.seven_day} />
              <Metric label="5h" win={p.five_hour} />
            </div>
            {/* Same reasoning as the Codex row: the refill moment is the one
                thing a quota bar cannot express. Both windows get their own
                entry because they refill at different times — collapsing them
                into one date would be wrong for whichever window it isn't. */}
            <ResetLine
              entries={[
                ['7d', p.seven_day?.resets_at],
                ['5h', p.five_hour?.resets_at],
              ]}
            />
          </div>
        );
      })}
      {codex.length > 0 && (
        <>
          {/* Own sub-heading: Codex numbers are a different provider AND a
              different freshness class than the Anthropic ones above — running
              them together in one list would imply they're comparable. */}
          <div className="uw-sub">Codex</div>
          {codex.map((a, i) => (
            // Emphasis follows the server's `primary` flag, NOT the position.
            // If the backend ever returns no primary at all, nothing gets
            // falsely promoted just for standing first.
            <CodexRow key={a.email || a.source_bot || i} acc={a} primary={a.primary === true} />
          ))}
        </>
      )}
    </div>
  );
}
